/**
 * The time period a Teams utilisation figure covers (CWL-157).
 *
 * The figure used to be a period-less ratio, so nothing on screen said what it
 * measured. These helpers turn a period choice plus a navigation cursor into a
 * concrete half-open range that the backend can be asked for and the UI can
 * print, so the number and its period always travel together.
 *
 * Deliberately kept in the teams domain rather than reusing the Gantt's zoom
 * helpers (`ganttRangeStart` / `ganttDaysToShow`): the Gantt's `'hour'` zoom
 * still spans a whole day of hour columns, whereas utilisation needs a true
 * one-hour window.
 */
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  format,
  isSameMonth,
  isSameYear,
  isValid,
  parseISO,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';

export type UtilisationPeriod = 'hour' | 'day' | 'week' | 'month' | 'custom';

/** A half-open range: `start` inclusive, `end` exclusive — matches the API. */
export interface UtilisationRange {
  start: Date;
  end: Date;
}

/** The `?from=&to=` pair sent to the teams endpoints. */
export type UtilisationRangeQuery = {
  from: string;
  to: string;
};

export const UTILISATION_PERIODS: { value: UtilisationPeriod; label: string }[] = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'custom', label: 'Custom' },
];

/** Human name for a period, for the "Utilisation · this week" style label. */
export function periodDescription(period: UtilisationPeriod): string {
  switch (period) {
    case 'hour':
      return 'this hour';
    case 'day':
      return 'this day';
    case 'week':
      return 'this week';
    case 'month':
      return 'this month';
    case 'custom':
      return 'custom period';
  }
}

/** Parse an ISO date (or `yyyy-MM-dd`) string, or return null if unusable. */
function parseDateInput(value?: string): Date | null {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/**
 * Resolve the range for a period.
 *
 * `cursor` is the point the user has navigated to; each period snaps it to its
 * natural boundary (the hour, the day, Monday, the 1st) so stepping forwards and
 * backwards lands on whole periods rather than drifting.
 *
 * `custom` takes two inclusive calendar dates and yields
 * `[start of from, start of the day after to)`, so picking the same date twice
 * gives that whole day. An incomplete or inverted custom range falls back to the
 * week containing `cursor`, which is also the backend's default.
 */
export function resolvePeriodRange(
  period: UtilisationPeriod,
  cursor: Date,
  customFrom?: string,
  customTo?: string,
): UtilisationRange {
  switch (period) {
    case 'hour': {
      const start = startOfHour(cursor);
      return { start, end: addHours(start, 1) };
    }
    case 'day': {
      const start = startOfDay(cursor);
      return { start, end: addDays(start, 1) };
    }
    case 'month': {
      const start = startOfMonth(cursor);
      return { start, end: addMonths(start, 1) };
    }
    case 'custom': {
      const from = parseDateInput(customFrom);
      const to = parseDateInput(customTo);
      if (from && to && from <= to) {
        return { start: startOfDay(from), end: addDays(startOfDay(to), 1) };
      }
      break;
    }
  }

  const start = startOfWeek(cursor, { weekStartsOn: 1 });
  return { start, end: addWeeks(start, 1) };
}

/** Move the cursor one whole period. Custom ranges are pinned by their inputs. */
export function stepPeriod(period: UtilisationPeriod, cursor: Date, direction: 1 | -1): Date {
  switch (period) {
    case 'hour':
      return addHours(cursor, direction);
    case 'day':
      return addDays(cursor, direction);
    case 'week':
      return addWeeks(cursor, direction);
    case 'month':
      return addMonths(cursor, direction);
    case 'custom':
      return cursor;
  }
}

/** Format the inclusive span of a half-open range, e.g. "Mon 7 – Sun 13 Sep 2026". */
function formatDaySpan(start: Date, end: Date): string {
  // `end` is exclusive, so the last day the user is looking at is the day before.
  const lastDay = subDays(end, 1);

  if (format(start, 'yyyy-MM-dd') === format(lastDay, 'yyyy-MM-dd')) {
    return format(start, 'EEE d MMM yyyy');
  }
  if (isSameMonth(start, lastDay)) {
    return `${format(start, 'EEE d')} – ${format(lastDay, 'EEE d MMM yyyy')}`;
  }
  if (isSameYear(start, lastDay)) {
    return `${format(start, 'EEE d MMM')} – ${format(lastDay, 'EEE d MMM yyyy')}`;
  }
  return `${format(start, 'EEE d MMM yyyy')} – ${format(lastDay, 'EEE d MMM yyyy')}`;
}

/** The range as shown on screen next to the utilisation figure. */
export function formatPeriodLabel(period: UtilisationPeriod, range: UtilisationRange): string {
  switch (period) {
    case 'hour':
      return `${format(range.start, 'EEE d MMM yyyy, HH:mm')}–${format(range.end, 'HH:mm')}`;
    case 'month':
      return format(range.start, 'MMMM yyyy');
    default:
      return formatDaySpan(range.start, range.end);
  }
}

/** ISO bounds for the API. Sent as UTC instants, so the server clips identically. */
export function toRangeQuery(range: UtilisationRange): UtilisationRangeQuery {
  return { from: range.start.toISOString(), to: range.end.toISOString() };
}

/** `yyyy-MM-dd` for seeding the Custom inputs from the current cursor. */
export function toDateInputValue(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}
